import type {
	KickFlowConfig,
	OAuthAuthorizationParams,
	OAuthToken,
	OAuthTokenRequest,
} from '../types';
import { KickOAuthError, KickNetworkError } from './errors';
import { DebugLogger } from './logger';

/**
 * OAuth 2.1 Manager with PKCE support
 * Handles all OAuth flows for the Kick API
 */
export class OAuthManager {
	private token: OAuthToken | null = null;
	private tokenPromise: Promise<string> | null = null;
	private readonly logger: DebugLogger;

	constructor(
		private config: KickFlowConfig,
		debug: boolean = false
	) {
		this.logger = new DebugLogger(debug);
	}

	/**
	 * Generate OAuth authorization URL for user authentication
	 * 
	 * @param params - PKCE parameters from PKCEUtils.generateParams()
	 * @param scopes - OAuth scopes to request
	 * @returns Authorization URL for user to visit
	 */
	getAuthorizationUrl(
		params: OAuthAuthorizationParams,
		scopes: string[] = ['public']
	): string {
		if (!this.config.redirectUri) {
			throw new Error(
				'redirectUri is required for user authentication flow. For server-to-server, tokens are handled automatically.'
			);
		}

		const url = new URL(`${this.config.oauthUrl}/oauth/authorize`);

		url.searchParams.set('response_type', 'code');
		url.searchParams.set('client_id', this.config.clientId);
		url.searchParams.set('redirect_uri', this.config.redirectUri);
		url.searchParams.set('scope', scopes.join(' '));
		url.searchParams.set('code_challenge', params.codeChallenge);
		url.searchParams.set('code_challenge_method', 'S256');

		if (params.state) {
			url.searchParams.set('state', params.state);
		}

		const authUrl = url.toString();
		this.logger.info('Generated authorization URL', { scopes, state: params.state });

		return authUrl;
	}

	/**
	 * Exchange authorization code for access token
	 * 
	 * @param tokenRequest - Authorization code and verifier
	 * @returns OAuth token with access/refresh tokens
	 */
	async exchangeCodeForToken(tokenRequest: OAuthTokenRequest): Promise<OAuthToken> {
		if (!this.config.redirectUri) {
			throw new Error(
				'redirectUri is required for authorization code flow. For server-to-server, tokens are handled automatically.'
			);
		}

		try {
			const body = new URLSearchParams({
				grant_type: 'authorization_code',
				client_id: this.config.clientId,
				client_secret: this.config.clientSecret,
				redirect_uri: this.config.redirectUri,
				code_verifier: tokenRequest.codeVerifier,
				code: tokenRequest.code,
			});

			const url = `${this.config.oauthUrl}/oauth/token`;
			this.logger.oauth('Token Exchange', url, body.toString());

			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: body.toString(),
			});

			if (!response.ok) {
				const responseBody = await this.parseErrorResponse(response);
				this.logger.oauth('Token Exchange Error', url, undefined, {
					status: response.status,
					body: responseBody,
				});

				throw new KickOAuthError(
					`Token exchange failed: ${response.status} ${response.statusText}`,
					response.status,
					responseBody
				);
			}

			const data = await response.json();
			this.token = this.createTokenFromResponse(data);

			this.logger.oauth('Token Exchange Success', url);
			return this.token;
		} catch (error) {
			if (error instanceof KickOAuthError) {
				throw error;
			}
			throw new KickNetworkError(
				'Failed to connect to OAuth endpoint',
				error as Error
			);
		}
	}

	/**
	 * Set token manually (useful for stored tokens)
	 * 
	 * @param token - OAuth token to set
	 */
	setToken(token: OAuthToken): void {
		this.token = token;
		this.logger.info('Token set manually');
	}

	/**
	 * Get current token (may trigger automatic refresh)
	 * 
	 * @returns Promise resolving to access token string
	 */
	async getAccessToken(): Promise<string> {
		if (this.tokenPromise) {
			return this.tokenPromise;
		}

		if (this.token && this.isTokenValid()) {
			return this.token.accessToken;
		}

		this.tokenPromise = this.autoRefreshToken();
		try {
			const token = await this.tokenPromise;
			return token;
		} finally {
			this.tokenPromise = null;
		}
	}

	/**
	 * Get current token object (without triggering refresh)
	 * 
	 * @returns Current token or null
	 */
	getCurrentToken(): OAuthToken | null {
		return this.token;
	}

	/**
	 * Check if current token is valid
	 * 
	 * @returns True if token exists and is not expired
	 */
	isTokenValid(): boolean {
		if (!this.token) return false;
		// Add 60 second buffer to account for request time
		return Date.now() < this.token.expiresAt - 60000;
	}

	/**
	 * Clear current token
	 */
	clearToken(): void {
		this.token = null;
		this.tokenPromise = null;
		this.logger.info('Token cleared');
	}

	/**
	 * Automatically refresh or get new token
	 */
	private async autoRefreshToken(): Promise<string> {
		if (this.token?.refreshToken) {
			try {
				return await this.refreshAccessToken();
			} catch (error) {
				this.logger.tokenRefresh(
					'Token refresh failed, getting new token',
					error as Error
				);
			}
		}

		if (!this.config.redirectUri) {
			return await this.getClientCredentialsToken();
		}

		throw new KickOAuthError(
			'No valid token available. For user authentication, use exchangeCodeForToken() first. For server-to-server, omit redirectUri from config.',
			401
		);
	}

	/**
	 * Get client credentials token (server-to-server)
	 */
	private async getClientCredentialsToken(): Promise<string> {
		try {
			const body = new URLSearchParams({
				grant_type: 'client_credentials',
				client_id: this.config.clientId,
				client_secret: this.config.clientSecret,
			});

			const url = `${this.config.oauthUrl}/oauth/token`;
			this.logger.oauth('Client Credentials', url, body.toString());

			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: body.toString(),
			});

			if (!response.ok) {
				const responseBody = await this.parseErrorResponse(response);
				this.logger.oauth('Client Credentials Error', url, undefined, {
					status: response.status,
					body: responseBody,
				});

				throw new KickOAuthError(
					`Client credentials token request failed: ${response.status} ${response.statusText}`,
					response.status,
					responseBody
				);
			}

			const data = await response.json();
			this.token = this.createTokenFromResponse(data);

			this.logger.oauth('Client Credentials Success', url);
			return this.token.accessToken;
		} catch (error) {
			if (error instanceof KickOAuthError) {
				throw error;
			}
			throw new KickNetworkError(
				'Failed to get client credentials token',
				error as Error
			);
		}
	}

	/**
	 * Refresh access token using refresh token
	 */
	private async refreshAccessToken(): Promise<string> {
		if (!this.token?.refreshToken) {
			throw new KickOAuthError('No refresh token available', 401);
		}

		try {
			const body = new URLSearchParams({
				grant_type: 'refresh_token',
				client_id: this.config.clientId,
				client_secret: this.config.clientSecret,
				refresh_token: this.token.refreshToken,
			});

			const url = `${this.config.oauthUrl}/oauth/token`;
			this.logger.oauth('Token Refresh', url, body.toString());

			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: body.toString(),
			});

			if (!response.ok) {
				const responseBody = await this.parseErrorResponse(response);
				this.logger.oauth('Token Refresh Error', url, undefined, {
					status: response.status,
					body: responseBody,
				});

				throw new KickOAuthError(
					`Token refresh failed: ${response.status} ${response.statusText}`,
					response.status,
					responseBody
				);
			}

			const data = await response.json();
			this.token = {
				...this.createTokenFromResponse(data),
				// Keep old refresh token if new one not provided
				refreshToken: data.refresh_token || this.token.refreshToken,
			};

			this.logger.oauth('Token Refresh Success', url);
			return this.token.accessToken;
		} catch (error) {
			if (error instanceof KickOAuthError) {
				throw error;
			}
			throw new KickNetworkError('Failed to refresh token', error as Error);
		}
	}

	/**
	 * Parse error response body safely
	 */
	private async parseErrorResponse(response: Response): Promise<unknown> {
		try {
			const text = await response.text();
			try {
				return JSON.parse(text);
			} catch {
				return text;
			}
		} catch {
			return 'Unable to read response body';
		}
	}

	/**
	 * Create token object from OAuth response
	 */
	private createTokenFromResponse(data: Record<string, unknown>): OAuthToken {
		return {
			accessToken: data.access_token as string,
			tokenType: data.token_type as string,
			expiresIn: data.expires_in as number,
			refreshToken: data.refresh_token as string | undefined,
			scope: data.scope as string | undefined,
			expiresAt: Date.now() + (data.expires_in as number) * 1000,
		};
	}
}
